import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, XCircle, Mail, Save, Send, Server, Trash2, Download } from "lucide-react";
import AdminEmailTemplates from "../../components/admin/AdminEmailTemplates";

const CONTEXT_LABELS = {
  bo_credentials: "Credenciais (backoffice)",
  credential_single: "Credencial individual",
  credential_bulk: "Credenciais em lote",
  enrollment_confirmation: "Confirmação de inscrição",
  enrollment_credentials: "Credenciais da inscrição",
  smtp_test: "Teste de SMTP",
};

export default function AdminEmails() {
  const [data, setData] = useState({ items: [], total: 0, failed: 0 });
  const [loading, setLoading] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState("all"); // all | ok | failed

  const loadLogs = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email-logs");
      setData(data);
      setSelected(new Set());
    } catch (e) { toast.error("Erro ao carregar registos"); }
    setLoading(false);
  };
  const loadCfg = async () => {
    try {
      const { data } = await api.get("/admin/smtp-config");
      setCfg({ ...data, pt_pass: "", en_pass: "", sendgrid_api_key: "" });
    } catch (e) { toast.error("Erro ao carregar configuração de email"); }
  };
  useEffect(() => { loadLogs(); loadCfg(); }, []);

  const setF = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const saveCfg = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg };
      // only send secrets if the admin typed a new one
      if (!payload.pt_pass) delete payload.pt_pass;
      if (!payload.en_pass) delete payload.en_pass;
      if (!payload.sendgrid_api_key) delete payload.sendgrid_api_key;
      delete payload.pt_pass_set; delete payload.en_pass_set; delete payload.using_db_config; delete payload.sendgrid_key_set;
      const { data } = await api.put("/admin/smtp-config", payload);
      setCfg({ ...data, pt_pass: "", en_pass: "", sendgrid_api_key: "" });
      toast.success("Configuração de email guardada");
    } catch (e) { toast.error("Erro ao guardar"); }
    setSaving(false);
  };

  const sendTest = async (lang) => {
    if (!testTo.includes("@")) { toast.error("Indique um email de destino válido"); return; }
    setTesting(true);
    try {
      const { data } = await api.post("/admin/smtp-config/test", { to: testTo, lang });
      if (data.ok) toast.success("Email de teste enviado com sucesso! ✅");
      else toast.error(`Falha: ${data.error || "erro desconhecido"}`);
      loadLogs();
    } catch (e) { toast.error("Erro ao enviar teste"); }
    setTesting(false);
  };

  const toggleOne = (id) => setSelected((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const visible = data.items.filter((e) => filter === "all" || (filter === "ok" ? e.ok : !e.ok));
  const allSelected = visible.length > 0 && visible.every((e) => selected.has(e.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(visible.map((e) => e.id)));

  const deleteOne = async (id) => {
    if (!window.confirm("Eliminar este registo de envio?")) return;
    try {
      await api.delete(`/admin/email-logs/${id}`);
      toast.success("Registo eliminado");
      loadLogs();
    } catch (e) { toast.error("Erro ao eliminar"); }
  };
  const resend = async (id) => {
    if (!window.confirm("Reenviar este email com o mesmo conteúdo?")) return;
    try {
      const { data: r } = await api.post(`/admin/email-logs/${id}/resend`);
      if (r.ok) toast.success("Email reenviado");
      else toast.error("O reenvio falhou (ver registo)");
      loadLogs();
    } catch (e) { toast.error(e.response?.data?.detail || "Não foi possível reenviar"); }
  };
  const deleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Eliminar ${selected.size} registo(s) selecionado(s)?`)) return;
    try {
      const { data: r } = await api.post("/admin/email-logs/delete", { ids: Array.from(selected) });
      toast.success(`${r.deleted} registo(s) eliminado(s)`);
      loadLogs();
    } catch (e) { toast.error("Erro ao eliminar"); }
  };
  const exportCsv = () => {
    const rows = (selected.size > 0 ? visible.filter((e) => selected.has(e.id)) : visible);
    if (rows.length === 0) { toast.error("Sem registos para exportar"); return; }
    const headers = ["estado", "data", "para", "cc", "tipo", "assunto", "erro"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [headers.join(",")].concat(rows.map((e) => [
      e.ok ? "OK" : "Falha",
      (e.created_at || "").slice(0, 19).replace("T", " "),
      e.to || "",
      e.cc || "",
      CONTEXT_LABELS[e.context] || e.context || "",
      e.subject || "",
      e.error || "",
    ].map(esc).join(",")));
    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `registo-envios-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-emails-page" className="space-y-6">
      <button onClick={saveCfg} disabled={saving} data-testid="emails-save-floating"
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#005F73] text-white font-bold shadow-2xl hover:bg-[#00485a] transition-colors disabled:opacity-50">
        <Save size={18}/> {saving ? "A guardar..." : "Guardar configuração"}
      </button>
      <div>
        <h1 className="font-outfit text-3xl font-bold text-[#0B1D3A]">Emails</h1>
        <p className="text-sm text-[#0B1D3A]/60 mt-1">Escolhe o fornecedor de envio (SMTP ou SendGrid), configura as credenciais e audita todos os envios.</p>
      </div>

      {/* SMTP configuration */}
      {cfg && (
        <div className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5 space-y-4" data-testid="smtp-config-card">
          <h2 className="font-outfit font-bold text-[#0B1D3A] flex items-center gap-2"><Server size={18} className="text-[#005F73]" /> Configuração de envio de email</h2>
          <p className="text-xs text-[#0B1D3A]/55">Estes dados são guardados de forma segura na base de dados e usados em produção. As palavras-passe e a chave API nunca são mostradas — deixa em branco para manter a atual.</p>

          <div className="rounded-xl bg-[#F1F5F9] p-4 space-y-3" data-testid="email-provider-card">
            <label className="block text-xs font-semibold text-[#0B1D3A]">Fornecedor de email ativo
              <select value={cfg.provider || "smtp"} onChange={(e) => setF("provider", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 bg-white text-sm" data-testid="email-provider-select">
                <option value="smtp">SMTP (servidor próprio)</option>
                <option value="sendgrid">SendGrid (API)</option>
              </select>
            </label>
            {cfg.provider === "sendgrid" && (
              <>
              <label className="block text-xs">Chave API do SendGrid
                <input type="password" value={cfg.sendgrid_api_key || ""} onChange={(e) => setF("sendgrid_api_key", e.target.value)} placeholder={cfg.sendgrid_key_set ? "•••••••• (definida)" : "SG.xxxxx"} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm" data-testid="sendgrid-api-key" />
                <span className="block mt-1 text-[#0B1D3A]/50">O remetente (From) PT/EN abaixo tem de ser um remetente verificado no SendGrid.</span>
              </label>
              <label className="block text-xs font-semibold text-[#0B1D3A]">Região da conta SendGrid
                <select value={cfg.sendgrid_region || "global"} onChange={(e) => setF("sendgrid_region", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 bg-white text-sm" data-testid="sendgrid-region-select">
                  <option value="global">Global (EUA) — api.sendgrid.com</option>
                  <option value="eu">Europa (EU) — api.eu.sendgrid.com</option>
                </select>
                <span className="block mt-1 text-[#0B1D3A]/50">Se a conta foi criada com residência de dados na Europa, escolhe "Europa (EU)". Caso contrário dá erro 401.</span>
              </label>
              </>
            )}
          </div>

          {cfg.provider === "smtp" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="block text-xs md:col-span-2">Servidor SMTP (host)<input value={cfg.host || ""} onChange={(e) => setF("host", e.target.value)} placeholder="mail.exemplo.com" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm" data-testid="smtp-host" /></label>
            <label className="block text-xs">Porta<input type="number" value={cfg.port || ""} onChange={(e) => setF("port", parseInt(e.target.value) || 0)} placeholder="465" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm" data-testid="smtp-port" /></label>
          </div>
          )}
          {cfg.provider === "smtp" && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!cfg.use_ssl} onChange={(e) => setF("use_ssl", e.target.checked)} data-testid="smtp-ssl" />
            Usar SSL (porta 465). Desmarca para STARTTLS (porta 587).
          </label>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-2">
            <div className="space-y-2 rounded-xl bg-[#F8FAFC] p-4">
              <p className="font-semibold text-sm text-[#0B1D3A]">Conta Português (PT)</p>
              <label className="block text-xs">Utilizador<input value={cfg.pt_user || ""} onChange={(e) => setF("pt_user", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm" data-testid="smtp-pt-user" /></label>
              <label className="block text-xs">Palavra-passe<input type="password" value={cfg.pt_pass || ""} onChange={(e) => setF("pt_pass", e.target.value)} placeholder={cfg.pt_pass_set ? "•••••••• (definida)" : "não definida"} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm" data-testid="smtp-pt-pass" /></label>
              <label className="block text-xs">Remetente (From)<input value={cfg.pt_from || ""} onChange={(e) => setF("pt_from", e.target.value)} placeholder="NATALMÁTICO <natal@exemplo.com>" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm" data-testid="smtp-pt-from" /></label>
            </div>
            <div className="space-y-2 rounded-xl bg-[#F8FAFC] p-4">
              <p className="font-semibold text-sm text-[#0B1D3A]">Conta Inglês (EN)</p>
              <label className="block text-xs">Utilizador<input value={cfg.en_user || ""} onChange={(e) => setF("en_user", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm" data-testid="smtp-en-user" /></label>
              <label className="block text-xs">Palavra-passe<input type="password" value={cfg.en_pass || ""} onChange={(e) => setF("en_pass", e.target.value)} placeholder={cfg.en_pass_set ? "•••••••• (definida)" : "não definida"} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm" data-testid="smtp-en-pass" /></label>
              <label className="block text-xs">Remetente (From)<input value={cfg.en_from || ""} onChange={(e) => setF("en_from", e.target.value)} placeholder="X-MATHS Challenge <maths@exemplo.com>" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm" data-testid="smtp-en-from" /></label>
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-3 pt-2 border-t border-[#0B1D3A]/5">
            <button onClick={saveCfg} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#005F73] text-white font-semibold text-sm disabled:opacity-50" data-testid="smtp-save-btn"><Save size={15} /> {saving ? "A guardar..." : "Guardar configuração"}</button>
            <div className="flex items-end gap-2">
              <label className="block text-xs">Email de teste<input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="o-teu@email.com" className="mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm" data-testid="smtp-test-to" /></label>
              <button onClick={() => sendTest("pt")} disabled={testing} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FF6B6B] text-white font-semibold text-sm disabled:opacity-50" data-testid="smtp-test-btn"><Send size={15} /> {testing ? "A enviar..." : "Enviar teste"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Rodapé dos emails (aparece no fim de todos os emails) */}
      {cfg && (
        <div className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5 space-y-4 mt-6" data-testid="email-footer-card">
          <h2 className="font-outfit font-bold text-[#0B1D3A] flex items-center gap-2"><Server size={18} className="text-[#005F73]" /> Rodapé dos emails</h2>
          <p className="text-xs text-[#0B1D3A]/60">Texto que aparece no final de <b>todos</b> os emails enviados (por baixo do conteúdo). Ideal para: nome da organização, morada, contactos, website, aviso legal. Podes usar HTML simples (ex.: <code>&lt;a href="https://..."&gt;site&lt;/a&gt;</code>). Deixa vazio para não mostrar rodapé.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="block text-sm">🇵🇹 Rodapé (Português Europeu)
              <textarea rows="6" value={cfg.footer_pt || ""} onChange={(e) => setF("footer_pt", e.target.value)} placeholder={"NATALMÁTICO — Eudactica, Lda.\nRua Exemplo, 000, 0000-000 Cidade\nsuporte@eudactica.com · +351 000 000 000\nwww.eudactica.com"} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm font-mono" data-testid="email-footer-pt" />
            </label>
            <label className="block text-sm">🇧🇷 Rodapé (Português Brasil)
              <textarea rows="6" value={cfg.footer_br || ""} onChange={(e) => setF("footer_br", e.target.value)} placeholder={"Se vazio, usa o rodapé de Português Europeu."} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm font-mono" data-testid="email-footer-br" />
            </label>
            <label className="block text-sm">🇬🇧 Footer (English)
              <textarea rows="6" value={cfg.footer_en || ""} onChange={(e) => setF("footer_en", e.target.value)} placeholder={"X-MATHS Challenge — Eudactica, Lda.\nExample Street, 000, City\nsupport@eudactica.com · +351 000 000 000\nwww.eudactica.com"} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm font-mono" data-testid="email-footer-en" />
            </label>
          </div>
          <button onClick={saveCfg} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#005F73] text-white font-semibold text-sm disabled:opacity-50" data-testid="email-footer-save-btn"><Save size={15} /> {saving ? "A guardar..." : "Guardar rodapé"}</button>
        </div>
      )}


      {/* Editable email templates */}
      <AdminEmailTemplates />

      {/* Logs */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="font-outfit font-bold text-[#0B1D3A]">Registo de envios</h2>
          <div className="inline-flex rounded-lg bg-[#F1F5F9] p-0.5 text-xs font-semibold" data-testid="emails-filter">
            {[["all", "Todos"], ["ok", "Enviados"], ["failed", "Falhas"]].map(([v, label]) => (
              <button key={v} onClick={() => { setFilter(v); setSelected(new Set()); }} className={`px-3 py-1.5 rounded-md transition-colors ${filter === v ? "bg-white shadow-sm text-[#0B1D3A]" : "text-[#0B1D3A]/55"}`} data-testid={`emails-filter-${v}`}>{label}</button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#005F73] text-white font-semibold text-sm" data-testid="emails-export-btn">
            <Download size={16} /> Exportar{selected.size > 0 ? ` (${selected.size})` : ""}
          </button>
          <button onClick={deleteSelected} disabled={selected.size === 0} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white font-semibold text-sm disabled:opacity-40" data-testid="emails-delete-selected-btn">
            <Trash2 size={16} /> Eliminar{selected.size > 0 ? ` (${selected.size})` : ""}
          </button>
          <button onClick={loadLogs} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0B1D3A] text-white font-semibold text-sm" data-testid="emails-refresh-btn">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Atualizar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5">
          <p className="text-xs text-[#0B1D3A]/60 uppercase font-bold">Total</p>
          <p className="font-fredoka text-3xl font-bold text-[#0B1D3A]" data-testid="emails-total">{data.total}</p>
        </div>
        <div className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5">
          <p className="text-xs text-[#0B1D3A]/60 uppercase font-bold">Enviados</p>
          <p className="font-fredoka text-3xl font-bold text-[#2D9C5A]">{data.total - data.failed}</p>
        </div>
        <div className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5">
          <p className="text-xs text-[#0B1D3A]/60 uppercase font-bold">Falhas</p>
          <p className="font-fredoka text-3xl font-bold text-[#E63946]" data-testid="emails-failed">{data.failed}</p>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-[#0B1D3A]/5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC] text-[#0B1D3A]/70">
            <tr>
              <th className="px-4 py-3 w-10"><input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="emails-select-all" /></th>
              <th className="text-left px-4 py-3">Estado</th>
              <th className="text-left px-4 py-3">Data</th>
              <th className="text-left px-4 py-3">Para</th>
              <th className="text-left px-4 py-3">Tipo</th>
              <th className="text-left px-4 py-3">Assunto</th>
              <th className="text-left px-4 py-3">Erro</th>
              <th className="text-left px-4 py-3 w-24">Ações</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((e, i) => (
              <tr key={e.id} className={`border-t border-[#0B1D3A]/5 ${selected.has(e.id) ? "bg-[#005F73]/5" : ""}`} data-testid={`email-row-${i}`}>
                <td className="px-4 py-3"><input type="checkbox" checked={selected.has(e.id)} onChange={() => toggleOne(e.id)} data-testid={`email-select-${i}`} /></td>
                <td className="px-4 py-3">
                  {e.ok
                    ? <span className="inline-flex items-center gap-1 text-xs text-[#2D9C5A] font-semibold"><CheckCircle2 size={14}/> OK</span>
                    : <span className="inline-flex items-center gap-1 text-xs text-[#E63946] font-semibold"><XCircle size={14}/> Falha</span>}
                </td>
                <td className="px-4 py-3 text-[#0B1D3A]/60 whitespace-nowrap">{(e.created_at || "").slice(0, 19).replace("T", " ")}</td>
                <td className="px-4 py-3">{e.to}{e.cc ? <span className="text-[#0B1D3A]/40"> (cc {e.cc})</span> : null}</td>
                <td className="px-4 py-3 text-[#0B1D3A]/70">{CONTEXT_LABELS[e.context] || e.context || "—"}</td>
                <td className="px-4 py-3 text-[#0B1D3A]/70">{e.subject}</td>
                <td className="px-4 py-3 text-[#E63946] text-xs max-w-xs truncate" title={e.error}>{e.error || ""}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <button onClick={() => resend(e.id)} className="text-[#005F73] hover:bg-[#005F73]/10 p-1.5 rounded" title="Reenviar email" data-testid={`email-resend-${i}`}><Send size={15} /></button>
                    <button onClick={() => deleteOne(e.id)} className="text-[#E63946] hover:bg-red-50 p-1.5 rounded" title="Eliminar" data-testid={`email-delete-${i}`}><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {visible.length === 0 && <tr><td colSpan="8" className="px-4 py-8 text-center text-[#0B1D3A]/50"><Mail size={24} className="mx-auto mb-2 opacity-40"/>Sem registos de email{filter === "ok" ? " enviados" : filter === "failed" ? " com falha" : ""}.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
